import {
    CalendarMonthOutlined,
    CallEndRounded,
    DeleteForeverRounded,
    HeadphonesRounded,
    MoreVertRounded,
    TouchApp,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import ShowMoreText from 'react-show-more-text';
import {
    useDeleteListDataMutation,
    useUpdateListDataMutation,
} from '../../api/apiSlice';
import useAuth from '../../hooks/useAuth';
import errorSnackbar from '../../utils/errorSnackbar';
import ConfirmSheet from './ConfirmSheet';

export default function ItemPastorCrusade({ theme, crusade, onDelete }) {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    const [deleteCrusade, { isLoading }] = useDeleteListDataMutation();
    const [updateCrusade, { isLoading: isPutLoading }] =
        useUpdateListDataMutation();

    const {
        id: crusadeId,
        dates,
        description,
        media,
        title,
        tag,
        status,
        interactions,
    } = crusade;

    const crusadeActions = async (endpoint, actionFunc) => {
        const { data, error } = await actionFunc({
            list: `${endpoint}/${crusadeId}`,
        });

        errorSnackbar(error, enqueueSnackbar);

        if (data) {
            enqueueSnackbar('Operation successful');
            onDelete(true);
        }

        setAnchorEl(null);
    };

    return (
        <Grid item xs={12}>
            <Card variant="outlined">
                <CardContent sx={{ bgcolor: theme }}>
                    <Stack
                        sx={{ mb: 2 }}
                        direction="row"
                        className="text-white"
                        justifyContent="space-between"
                    >
                        {status == 'live' ? (
                            <Stack direction="row" alignItems="center">
                                <Box
                                    className="bg-danger"
                                    sx={{
                                        borderRadius: '50%',
                                        width: 10,
                                        height: 10,
                                        mr: 1,
                                    }}
                                />
                                <Typography className="text-white text-success fw-bold">
                                    Live
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack direction="row" alignItems="center">
                                <CalendarMonthOutlined
                                    sx={{ mr: 1, width: 36, height: 36 }}
                                />
                                <Stack>
                                    <Typography
                                        className="fw-bold"
                                        variant="body2"
                                    >
                                        {moment(dates?.start).format('ll')}
                                    </Typography>
                                    <Typography
                                        className="fw-bold"
                                        variant="body2"
                                    >
                                        {moment(dates?.start).format('LT')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                        <IconButton
                            color="inherit"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                            <MoreVertRounded />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={() => setAnchorEl(null)}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                        >
                            {!(status == 'live') && (
                                <MenuItem
                                    onClick={async () => {
                                        const { data, error } =
                                            await updateCrusade({
                                                list: `/crusades/${crusadeId}`,
                                                body: {
                                                    meeting_id:
                                                        Date.now() +
                                                        user?.username,
                                                },
                                            });

                                        errorSnackbar(error, enqueueSnackbar);

                                        if (data) {
                                            enqueueSnackbar("You're Live!");
                                            onDelete(true);
                                        }

                                        setAnchorEl(null);
                                    }}
                                >
                                    <ListItemIcon>
                                        <TouchApp />
                                    </ListItemIcon>
                                    <ListItemText>Go Live</ListItemText>
                                </MenuItem>
                            )}
                            {status == 'live' && (
                                <MenuItem
                                    onClick={() =>
                                        crusadeActions(
                                            '/crusades',
                                            deleteCrusade
                                        )
                                    }
                                >
                                    <ListItemIcon>
                                        <CallEndRounded color="warning" />
                                    </ListItemIcon>
                                    <ListItemText primary="End" />
                                </MenuItem>
                            )}
                            <MenuItem
                                onClick={() => {
                                    setDeleteModalOpen(true);
                                    setAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <DeleteForeverRounded color="secondary" />
                                </ListItemIcon>
                                <ListItemText primary="Delete" />
                            </MenuItem>
                        </Menu>
                    </Stack>
                    <Stack
                        direction="row"
                        className="text-white"
                        justifyContent="space-between"
                    >
                        <div>
                            <Typography variant="body2" className="fw-bold">
                                #{tag}
                            </Typography>
                            <Typography variant="h5">{title}</Typography>
                        </div>
                        <Box
                            sx={{
                                ml: 1,
                                width: 80,
                                height: 50,
                                borderRadius: 0.5,
                                backgroundImage: `url(${media?.link})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                        />
                    </Stack>
                    <ConfirmSheet
                        open={deleteModalOpen}
                        isLoading={isLoading || isPutLoading}
                        onCancel={() => setDeleteModalOpen(false)}
                        onConfirm={() => {
                            crusadeActions('/op/crusades', deleteCrusade);
                            setDeleteModalOpen(false);
                        }}
                    />
                    {status == 'live' && (
                        <Box
                            sx={{ my: 0.5 }}
                            className="d-flex text-white align-items-center"
                        >
                            <HeadphonesRounded sx={{ mr: 1 }} />
                            <Typography
                                variant="body2"
                                className="d-flex fw-bold  align-items-center"
                                sx={{ mt: 1 }}
                            >
                                {interactions?.listening} tuned in
                            </Typography>
                        </Box>
                    )}
                </CardContent>
                <Divider />
                <CardContent className="text-white" sx={{ bgcolor: '#545454' }}>
                    <ShowMoreText lines={2} more="more" less="less">
                        {description}
                    </ShowMoreText>
                </CardContent>
            </Card>
        </Grid>
    );
}
